// Con qué método entró el usuario por última vez EN ESTE DISPOSITIVO, para marcarlo
// con "Último utilizado" en la pantalla de login y avisar en el registro que acá ya
// hay una cuenta en uso (ADR-2026-08-11-ultimo-metodo-de-ingreso-se-registra-global).
//
// Vive en localStorage y **no se borra al desloguear**: recordar entre sesiones es
// justamente la feature. No guarda el mail a propósito — en un dispositivo compartido
// mostrarlo filtraría quién entró.
//
// Best-effort TOTAL, igual que `signupAttribution`: nunca lanza. En modo privado o con
// la cuota llena el localStorage tira, y esto jamás debe romper un login.

import type { AMREntry, Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export type MetodoLogin = 'email' | 'google';

const KEY = 'atlas:ultimo-login';

/** El método guardado, o `null` si nunca se entró acá (o el valor está corrupto). */
export function leerUltimoLogin(): MetodoLogin | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'email' || v === 'google' ? v : null;
  } catch {
    return null;
  }
}

export function guardarUltimoLogin(metodo: MetodoLogin): void {
  try {
    localStorage.setItem(KEY, metodo);
  } catch {}
}

/** Payload del JWT ya parseado, o `null` si el token no se puede decodificar. */
function payloadDelToken(token: string): { amr?: AMREntry[] } | null {
  try {
    const base64url = token.split('.')[1];
    if (!base64url) return null;
    // `atob` tolera base64url sin padding, pero no los caracteres `-` y `_`.
    const binaria = atob(base64url.replace(/-/g, '+').replace(/_/g, '/'));
    // `atob` devuelve una cadena BINARIA (un byte por char). Hay que releerla como
    // UTF-8: el payload trae `user_metadata`, y un nombre con acentos rompería el
    // `JSON.parse` si se interpretara como latin1.
    const bytes = Uint8Array.from(binaria, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Con qué método se autenticó **esta** sesión.
 *
 * Sale del claim `amr` del JWT (Authentication Methods Reference), que es el único
 * dato que habla del ingreso actual. Se decodifica el token a mano, en vez de pedirlo
 * con `supabase.auth.mfa.getAuthenticatorAssuranceLevel()`, por dos razones: esa
 * función toma el lock de auth y llamarla desde adentro de un callback de
 * `onAuthStateChange` es el patrón que Supabase desaconseja explícitamente; y siendo
 * sincrónica, acá no hay promesa que pueda quedar colgada en silencio.
 *
 * `app_metadata.provider` queda de fallback solamente: refleja el proveedor con el que
 * se **creó** la cuenta, no el de este ingreso, así que en una cuenta con identidades
 * linkeadas (alta por mail, ingreso por Google) marcaría el camino equivocado.
 *
 * Google es el único OAuth configurado en Atlas, así que `oauth` → `google` sin
 * ambigüedad. Si mañana se suma otro proveedor, esta traducción deja de alcanzar.
 */
export function metodoDeLaSesion(session: Session): MetodoLogin {
  const amr = payloadDelToken(session.access_token)?.amr ?? [];
  // El más reciente: una cuenta con identidades linkeadas acumula varias entradas.
  const ultimo = amr.reduce<AMREntry | null>(
    (max, m) => (!max || m.timestamp > max.timestamp ? m : max),
    null,
  );
  if (ultimo?.method === 'oauth') return 'google';
  if (ultimo?.method === 'password') return 'email';
  // Sin `amr` usable: mismo criterio que ya usaban `Auth.tsx` y `AuthCompat` para
  // detectar OAuth (todo lo que no es 'email' se considera Google).
  return session.user.app_metadata?.provider === 'email' ? 'email' : 'google';
}

let suscripto = false;

/**
 * Registra el listener que persiste el método en cada ingreso. Se llama UNA vez desde
 * `main.tsx`, antes del render.
 *
 * Va acá y no en un componente a propósito: el login ocurre en `/auth`, que es una
 * ruta **pública**. Un listener montado dentro de `ProtectedRoute` —donde estaba en el
 * primer intento— todavía no existe cuando `SIGNED_IN` dispara, así que el evento pasa
 * sin que nadie lo escuche y no se escribe nada. Fuera de React el listener está vivo
 * desde el arranque, sin depender de qué ruta esté montada.
 *
 * El callback es sincrónico y no llama a ninguna API de Supabase: hacerlo desde adentro
 * puede trabar el lock de auth.
 */
export function iniciarSeguimientoUltimoLogin(): void {
  if (suscripto) return;
  suscripto = true;
  supabase.auth.onAuthStateChange((event, session) => {
    if (event !== 'SIGNED_IN' || !session) return;
    guardarUltimoLogin(metodoDeLaSesion(session));
  });
}
